import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { QUIZ_QUESTIONS } from '@/mocks/quiz-questions';
import { HEALTH_TIPS } from '@/mocks/jeux-data';

type Difficulty = 'Facile' | 'Moyen' | 'Difficile';

export default function QuizSante() {
  const navigate = useNavigate();
  const { startGame, endGame, gameStatus, gameScore, gameTime, gameErrors, updateScore, updateErrors, unlockAchievement } = useGame();
  const [difficulty, setDifficulty] = useState<Difficulty | null>(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [answered, setAnswered] = useState(false);
  const [showGameOver, setShowGameOver] = useState(false);
  const [answers, setAnswers] = useState<{ correct: boolean; qIndex: number }[]>([]);

  const questions = useMemo(() => {
    if (!difficulty) return [];
    const filtered = QUIZ_QUESTIONS.filter(q => q.difficulty === difficulty);
    return filtered.sort(() => Math.random() - 0.5).slice(0, 10);
  }, [difficulty]);

  const handleStart = (d: Difficulty) => {
    setDifficulty(d);
    setCurrentQ(0);
    setSelected(null);
    setAnswered(false);
    setShowGameOver(false);
    setAnswers([]);
    startGame('quiz-sante');
    updateScore(0);
    updateErrors(0);
  };

  const handleAnswer = useCallback((idx: number) => {
    if (answered) return;
    setSelected(idx);
    setAnswered(true);

    const q = questions[currentQ];
    const correct = idx === q.correctIndex;
    const newAnswers = [...answers, { correct, qIndex: currentQ }];
    setAnswers(newAnswers);

    if (correct) {
      updateScore(gameScore + 100);
    } else {
      updateErrors(gameErrors + 1);
    }
  }, [answered, currentQ, questions, answers, gameScore, gameErrors, updateScore, updateErrors]);

  const handleNext = useCallback(() => {
    if (currentQ + 1 >= questions.length) {
      const correctCount = answers.filter(a => a.correct).length;
      const finalScore = correctCount * 100 + (answers.length === correctCount ? 500 : 0);
      updateScore(finalScore);
      if (answers.every(a => a.correct)) unlockAchievement('perfect_quiz');
      endGame(finalScore, gameTime, gameErrors);
      setShowGameOver(true);
    } else {
      setCurrentQ(c => c + 1);
      setSelected(null);
      setAnswered(false);
    }
  }, [currentQ, questions.length, answers, gameScore, gameTime, gameErrors, updateScore, endGame, unlockAchievement]);

  const handleReplay = () => {
    if (difficulty) handleStart(difficulty);
  };

  const handleQuit = () => {
    endGame(gameScore, gameTime, gameErrors);
    navigate('/jeux', { replace: true });
  };

  if (!difficulty) {
    return (
      <GameWrapper gameName="Quiz Santé" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-question-answer-line text-3xl text-accent-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Quiz Santé</h2>
            <p className="text-foreground-600 text-sm mb-6">10 questions sur la santé</p>
            <div className="grid grid-cols-1 gap-3">
              {(['Facile', 'Moyen', 'Difficile'] as Difficulty[]).map((d) => (
                <button
                  key={d}
                  onClick={() => handleStart(d)}
                  className="py-3 px-4 bg-secondary-100 hover:bg-secondary-200 rounded-xl text-sm font-medium text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        </div>
      </GameWrapper>
    );
  }

  const q = questions[currentQ];
  const progress = ((currentQ + (answered ? 1 : 0)) / questions.length) * 100;

  return (
    <GameWrapper gameName="Quiz Santé" onQuit={handleQuit}>
      <div className="flex-1 p-4 max-w-2xl mx-auto w-full flex flex-col">
        {/* Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-foreground-600 mb-1.5">
            <span>Question {currentQ + 1}/{questions.length}</span>
            <span>{q.category} · {q.difficulty}</span>
          </div>
          <div className="h-1.5 bg-background-200 rounded-full overflow-hidden">
            <div className="h-full bg-primary-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Question */}
        <div className="bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200/70 mb-4">
          <h3 className="text-base md:text-lg font-semibold text-foreground-950 mb-5">{q.question}</h3>
          <div className="space-y-2.5">
            {q.options.map((opt, idx) => {
              let btnStyle = 'bg-background-100 border-background-200 hover:border-primary-500/50 text-foreground-950';
              if (answered) {
                if (idx === q.correctIndex) btnStyle = 'bg-green-100 border-green-400 text-green-800';
                else if (idx === selected) btnStyle = 'bg-red-100 border-red-400 text-red-800';
                else btnStyle = 'bg-background-100 border-background-200 text-foreground-500 opacity-60';
              }
              return (
                <button
                  key={idx}
                  onClick={() => handleAnswer(idx)}
                  disabled={answered}
                  className={`w-full text-left p-3.5 rounded-xl border-2 transition-all text-sm font-medium cursor-pointer ${btnStyle}`}
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-background-200 text-xs font-bold text-foreground-700 mr-3">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  {opt}
                  {answered && idx === q.correctIndex && <i className="ri-check-line text-green-600 float-right mt-0.5"></i>}
                  {answered && idx === selected && idx !== q.correctIndex && <i className="ri-close-line text-red-600 float-right mt-0.5"></i>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Explanation */}
        {answered && (
          <div className="bg-accent-100/50 border border-accent-200/50 rounded-xl p-4 mb-4 animate-in">
            <p className="text-sm text-accent-900 leading-relaxed">{q.explanation}</p>
            <button
              onClick={handleNext}
              className="mt-3 w-full py-2.5 bg-primary-500 text-background-50 rounded-xl font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {currentQ + 1 >= questions.length ? 'Voir les résultats' : 'Question suivante'}
            </button>
          </div>
        )}
      </div>

      {showGameOver && (
        <GameOverModal
          score={gameScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="quiz-sante"
          message={answers.filter(a => a.correct).length >= 8 ? 'Expert Santé !' : answers.filter(a => a.correct).length >= 5 ? 'Pas mal !' : 'Continue à apprendre !'}
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[Math.floor(Math.random() * HEALTH_TIPS.length)]}
        />
      )}
    </GameWrapper>
  );
}